import { type RunVisibility } from '@d2/schema';
import { type CharacterClass, type CharacterSummary } from '@d2/schema/characters';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

import { GameCanvas } from '../game/game-canvas';
import { type JoinRefusal } from '../game/join-refusal';
import { gameOrigin } from '../game/launch';
import { type RunToll } from '../game/run-end';
import { createRunDirectory, type RunDirectory } from '../game/runs';
import { LobbyBoard } from '../ui/lobby-board';
import { TownScreen } from '../ui/town-screen';
import { type RunOutcome, joinRefused, runEnded } from '../ui/town-screen-model';
import { townViewOf } from '../ui/town-view';
import { VendorCounter } from '../ui/vendor-counter';
import { CharacterScreen } from './CharacterScreen';
import { SignInScreen } from './SignInScreen';
import {
  chose,
  leftCharacter,
  restored,
  sessionEnded,
  signedIn,
  signedOut,
  startFrom,
  storableFrom,
  type AccountState,
} from './account-state';
import { createAccountApi, type AccountApi, type ApiRefusal, type ApiResult } from './api';
import { clearStoredSession, readStoredSession, writeStoredSession, type WebStorageLike } from './session-store';

/**
 * Which screen a visitor is on, and the only place in this client that holds a
 * token.
 *
 * Every *decision* it makes is somebody else's: `./account-state` owns what the
 * states are and how they follow one another, `./api` owns what the API says,
 * `./forms` owns what a form refuses. What is left here is genuinely React's
 * job — an effect that runs when a token changes, a promise whose answer may
 * arrive after the component is gone, and a re-render. That split is the reason
 * the interesting cases have unit tests at all: none of them is reachable by
 * clicking.
 *
 * ## A stored token is checked before anything is drawn from it
 *
 * A page load with a token in storage does not show the roster: it asks
 * `GET /api/auth/me` first. A client that skipped that would draw a character
 * screen for a session that had already ended and then blank it, and a player
 * whose token expired overnight would watch their characters disappear instead
 * of being told they were signed out.
 *
 * ## And a stored *character* is checked against the roster
 *
 * `apps/game` answers one refusal for an expired token and for a character that
 * is not there (`auth/seat.ts`), deliberately — so a client that walked straight
 * into the world with a remembered character id would read *this character was
 * deleted* as *you have been signed out*, and throw away a perfectly good
 * session. The roster is fetched first and the remembered id is looked up in it,
 * which is a question the API is willing to answer.
 *
 * ## Where the token lives
 *
 * `sessionStorage`, for the reasons written out in `./session-store.ts`. Read
 * once at mount and written on every change of state, so signing out cannot
 * leave a copy behind and a rejected token cannot be re-persisted.
 *
 * ## Town sits between the character and the world
 *
 * Choosing a character used to open a socket. It now opens **town**
 * (`../ui/town-screen.tsx`), which is the loop `docs/GAME-DESIGN.md` describes
 * — town → descend → floors → back to town — and the only screen from which a
 * run is a decision rather than a side effect of picking a name.
 *
 * That is a screen switch on state, exactly as the three above it are, and not
 * a router: there is no URL to own here (the query string is the *launch*, read
 * once by `game/launch.ts`) and nothing to go back to that this state does not
 * already say.
 *
 * A remembered character therefore lands in town rather than in a room, which
 * is a deliberate change to what a reload does. A stored `characterId` means
 * *this is who they chose*, never *this is who they were playing a second ago*
 * — and rejoining unasked is the surest way to meet the room's 409, since a
 * reload inside the reconnection window is precisely when the previous seat is
 * still held.
 *
 * ## A refused run comes back here
 *
 * `GameCanvas` hands up anything that is not a seat refusal (`onJoinRefused`),
 * and this is what makes that worth doing: the refusal lands on the one screen
 * with a button on it. A 409 — *this character is already in a run* — is no
 * longer a fatal panel reading "the game server could not be reached" over an
 * empty canvas; it is a sentence in town's live region with Descend live
 * underneath it (`TODO.md` #443). The client does **not** retry by itself, and
 * `game/join-refusal.ts` is where that decision is written out.
 *
 * ## And a run that **ended** comes back here too, with the roster behind it
 *
 * The player died, so the run is over and the seat left with close code 4200
 * (`game/run-end.ts`). It routes to exactly the same place a refusal does and
 * for a different reason: not because there is a button to press again, but
 * because town is where a character between runs *is*.
 *
 * Two things happen and the second one is still the point. Town is told, in
 * words that say a death rather than an outage (`runEndedText`) — and now with
 * the figures, because `SERVER_MESSAGE.runToll` carries what the room charged
 * and `GameCanvas` hands it up (`TODO.md` #486). And **the roster is read
 * again**, because the character that comes back is not the one that went down:
 * a quarter of the carried gold is forfeit along with everything picked up this
 * run (`docs/GAME-DESIGN.md`), and the row town was drawn from was fetched
 * before any of that happened.
 *
 * ## The toll and the purse are two facts, and this shell never joins them
 *
 * They answer different questions from different places: the toll says what was
 * **taken**, off a message the room sent the seat that paid it; the roster says
 * what **remains**, off the API that stores it. `heldRow.gold - toll.gold` is
 * not the second one and is not anything — the row this tab holds is *pre-run*
 * and the run had been adding to the purse ever since, while the forfeit is a
 * proportion of the purse as it stood at the moment of death. Neither number
 * knows about the other, so the answer comes from the roster and only from the
 * roster.
 *
 * **Nothing is drawn from the held row while that request is in flight**, and
 * the panel that does the not-drawing has *kept* its place here rather than
 * merely been left in. Its original reason survives the server-side fix that
 * looked like it: what `descent.room.ts` closed was the race between the
 * re-read and the character's write-back, so the roster's *answer* can no longer
 * be stale — but the *question* is still older than the run, since the row this
 * tab holds was fetched before the descent. Drawing it would put a pre-death
 * purse on screen for the length of one GET and then swap it without a word.
 *
 * The toll landing makes that worse rather than better, which is why the panel
 * stays. The live region now says *"it cost you 240 gold"* out loud, so a purse
 * beside it with those 240 still in it is not merely stale — it is a
 * contradiction a player can check with subtraction, in the one moment they have
 * every reason to be counting. The alternative that avoids both — subtracting
 * the toll from the held row to have something to show — is the arithmetic the
 * section above refuses.
 *
 * The one cost is unchanged: a roster read that is *refused* leaves this tab on
 * that panel, a pre-existing dead end (a restored session meets it too) that a
 * reload escapes, recorded in `TODO.md` #485 rather than fixed here.
 *
 * ## And a run somebody else started is opened from town too
 *
 * The lobby is a **board in town**, not a screen of its own, and the argument is
 * the same one that put town between the roster and the world: starting a run
 * and joining one are the same decision made two ways, and they belong on the
 * same screen with the same character standing on it. A lobby screen would be a
 * fourth state whose entire content is a list that is empty most of the time,
 * reachable only from town and leaveable only back to it — and it would have to
 * draw the purse and the level again to be any use, because *"would I survive
 * floor 12"* is a question about this character.
 *
 * What crosses the seam is a **room id**, and only ever after the board has
 * asked for one. The list carries no address (`@d2/schema`'s `RunListing`), so
 * a code is resolved at the moment of the press and the fullness check is the
 * one that is true then (`ui/lobby-board.tsx`). This shell re-checks nothing,
 * because there is nothing here that could: whether a seat is free is the room's
 * to know, and if it filled in the half-second since, the join is refused 522
 * and comes back as `run-gone` — which is a sentence about a run, not about a
 * server (`game/join-refusal.ts`).
 *
 * ## Who may find a run is the player's decision, and it is made once
 *
 * `onDescend` carries it, because the press and the choice are one act
 * (`ui/town-screen.tsx`). It reaches the create options and nothing else: a
 * joined room decided this when it was made, and there is deliberately no way
 * for this shell to send a visibility on a join.
 *
 * ## The vendor's counter stands in town, and hands the character back
 *
 * `../ui/vendor-counter.tsx` is mounted **as a child of the town screen**, which
 * is what makes it exist exactly as long as town does: descending unmounts it,
 * coming back mounts a new one, and a character who has just been down a floor
 * is therefore read again without this shell having to remember to invalidate
 * anything the way it does for the roster.
 *
 * It is handed a token, exactly as `GameCanvas` is, and it hands back one thing:
 * the **character the API answered an exchange with**. That is a whole
 * `CharacterSnapshot`, and a snapshot *is* a `CharacterSummary`, so the row this
 * roster holds is replaced by it wholesale. The gold bar a few lines above the
 * counter then moves to the balance the server wrote.
 *
 * Nothing here subtracts a price from a balance, and the reasoning is the same
 * one the death toll gets three paragraphs of above: the server's answer is the
 * fact and this tab's copy is older than it thinks. The seam is tighter than the
 * toll's — the answer to the very request that spent the gold carries the new
 * purse — which makes the arithmetic *more* tempting rather than less, since it
 * would usually agree. It would stop agreeing the moment two tabs, or a run's
 * flush, touched the same character.
 */

/** A storage that holds nothing, for a browser that will not give us one. */
const NOWHERE: WebStorageLike = {
  getItem: (): string | null => null,
  setItem: (): void => undefined,
  removeItem: (): void => undefined,
};

/**
 * The tab's own storage.
 *
 * Even *reaching* `sessionStorage` throws in a blocked third-party context, so
 * the access itself is guarded and not just the calls.
 */
const tabStorage = (): WebStorageLike => {
  try {
    return window.sessionStorage;
  } catch {
    return NOWHERE;
  }
};

/**
 * Where the API is for this build.
 *
 * Empty in production, where nginx proxies `/api/` to the API container from the
 * same origin. The e2e suite sets it because its API listens on a port of its
 * own (`playwright.config.ts`).
 */
const apiBase = (): string | null => {
  const configured = import.meta.env.VITE_API_URL?.trim() ?? '';

  return configured === '' ? null : configured;
};

/** The API client this build talks to. Built once — it holds no state, only a base URL. */
const buildApi = (): AccountApi => {
  const base = apiBase();

  // Spread rather than assigned: `exactOptionalPropertyTypes` makes an explicit
  // `undefined` a different thing from an absent key, and only the absent key
  // takes the default.
  return createAccountApi(base === null ? {} : { baseUrl: base });
};

/**
 * The **game** server's directory client, which is a different server from the
 * one above.
 *
 * `apps/api` owns the database and `apps/game` owns the live rooms, and only the
 * second can answer which runs are open — a run exists in a process, not in a
 * table. Its origin is derived from the socket URL this build was configured
 * with, so there is one place that knows where the game is (`game/launch.ts`).
 */
const buildRuns = (): RunDirectory =>
  createRunDirectory({ baseUrl: gameOrigin(import.meta.env.VITE_GAME_URL) });

/**
 * Which run this tab is in, and how it got there.
 *
 * Two kinds rather than a flag and a nullable id, because they are two
 * different journeys with different things to say: a run this tab **created**
 * carries the one thing only its creator chose — whether strangers may find it
 * — and one it **joined** carries the address the directory answered a code
 * with. Neither field is meaningful for the other kind, and a single shape
 * holding both would leave every reader to remember which half to trust.
 *
 * Deliberately not stored: a reload lands in town (see the header), and a
 * remembered room id would be this tab claiming a seat it cannot see.
 */
type Descent =
  | { readonly kind: 'created'; readonly visibility: RunVisibility }
  | { readonly kind: 'joined'; readonly roomId: string };

export const AccountGate = (): ReactElement => {
  const storage = useMemo(tabStorage, []);
  const api = useMemo<AccountApi>(buildApi, []);
  // Built once and held, so the board mounted in town is not handed a fresh
  // client on every render — which is a fresh identity, and therefore a fresh
  // read of the directory, on every keystroke anywhere on the screen.
  const runs = useMemo<RunDirectory>(buildRuns, []);

  const [state, setState] = useState<AccountState>(() => startFrom(readStoredSession(storage)));
  const [characters, setCharacters] = useState<readonly CharacterSummary[]>([]);
  const [rosterLoaded, setRosterLoaded] = useState(false);
  const [refusal, setRefusal] = useState<ApiRefusal | null>(null);
  const [busy, setBusy] = useState(false);
  /**
   * The run this tab is in, or `null` because it is standing in town.
   *
   * Two states and not a number of screens: a character has been chosen either
   * way, so the *account* state is the same and only what is drawn from it
   * differs. What it now carries is *which* run — see `Descent`. It is
   * deliberately not stored: a reload lands in town (see the header), and a
   * remembered room would be a claim about a seat this tab cannot see.
   */
  const [descent, setDescent] = useState<Descent | null>(null);
  /**
   * How the last press of Descend ended — refused, or over — or `null` because
   * none has. Town shows it; nothing else reads it.
   *
   * One value rather than two flags, so *"the room refused this"* and *"the run
   * is finished"* cannot both be standing at once and leave the screen to invent
   * a precedence between them (`ui/town-screen-model.ts`).
   */
  const [outcome, setOutcome] = useState<RunOutcome | null>(null);

  /** True while this component is mounted. A promise may land after a StrictMode remount. */
  const live = useRef(true);
  useEffect(() => {
    live.current = true;

    return (): void => {
      live.current = false;
    };
  }, []);

  // One writer for the stored session, driven by the state rather than by each
  // handler: a handler that forgot would leave a token behind, and a handler
  // that wrote in the wrong order would persist one that had just been dropped.
  useEffect(() => {
    const storable = storableFrom(state);

    if (storable === null) clearStoredSession(storage);
    else writeStoredSession(storage, storable);
  }, [state, storage]);

  const token = state.status === 'signed-out' ? null : state.token;

  /** Any authenticated call answering 401 means the session is over, from anywhere. */
  const handle = useCallback(<T,>(result: ApiResult<T>): T | null => {
    if (result.ok) {
      setRefusal(null);

      return result.value;
    }

    if (result.refusal.kind === 'unauthenticated') {
      setState(sessionEnded());
      setRefusal(null);
    } else {
      setRefusal(result.refusal);
    }

    return null;
  }, []);

  const endSession = useCallback(() => {
    setState(sessionEnded());
    setRefusal(null);
    // Whatever this tab was doing, it is not doing it any more. Left set, a
    // player who signed in again would be dropped straight into a socket.
    setDescent(null);
    setOutcome(null);
  }, []);

  // Checking a token found in storage. Runs once per stored token.
  useEffect(() => {
    if (state.status !== 'restoring') return;

    void (async (): Promise<void> => {
      const answer = await api.whoAmI(state.token);
      if (!live.current) return;

      if (answer.ok) setState((current) => restored(current, answer.value));
      // Every refusal is the same here, including an API that is not answering:
      // a token that cannot be checked cannot be used, and pretending otherwise
      // sends the player into a room that will refuse them anyway.
      else setState(sessionEnded());
    })();
  }, [api, state]);

  // The roster, whenever there is a session and it has not been read yet.
  useEffect(() => {
    if (state.status !== 'signed-in' || rosterLoaded) return;

    void (async (): Promise<void> => {
      const listed = await api.listCharacters(state.token);
      if (!live.current) return;

      const roster = handle(listed);
      if (roster === null) return;

      setCharacters(roster);
      setRosterLoaded(true);

      // A remembered character that is no longer on the account. Not a session
      // problem — see the header.
      setState((current) =>
        current.status === 'signed-in' &&
        current.characterId !== null &&
        !roster.some((character) => character.id === current.characterId)
          ? leftCharacter(current)
          : current,
      );
    })();
  }, [api, handle, rosterLoaded, state]);

  const attempt = useCallback(async (work: () => Promise<void>): Promise<void> => {
    setBusy(true);
    try {
      await work();
    } finally {
      if (live.current) setBusy(false);
    }
  }, []);

  const onSignIn = useCallback(
    (credentials: { email: string; password: string }): void => {
      void attempt(async () => {
        const result = await api.logIn(credentials);
        if (!live.current) return;

        if (result.ok) {
          setRefusal(null);
          setRosterLoaded(false);
          setState(signedIn(result.value, null));
        } else {
          // A login's 401 is a wrong password, never a lost session — so it is
          // shown on the form rather than routed through `handle`.
          setRefusal(result.refusal);
        }
      });
    },
    [api, attempt],
  );

  const onRegister = useCallback(
    (credentials: { email: string; password: string; displayName: string }): void => {
      void attempt(async () => {
        const result = await api.register(credentials);
        if (!live.current) return;

        if (result.ok) {
          setRefusal(null);
          setRosterLoaded(false);
          setState(signedIn(result.value, null));
        } else {
          setRefusal(result.refusal);
        }
      });
    },
    [api, attempt],
  );

  const onCreate = useCallback(
    (character: { name: string; characterClass: CharacterClass }): void => {
      if (token === null) return;

      void attempt(async () => {
        const created = handle(await api.createCharacter(token, character));
        if (!live.current || created === null) return;

        setCharacters((current) => [...current, created]);
      });
    },
    [api, attempt, handle, token],
  );

  const onDelete = useCallback(
    (characterId: string): void => {
      if (token === null) return;

      void attempt(async () => {
        const deleted = await api.deleteCharacter(token, characterId);
        if (!live.current) return;

        handle(deleted);

        // A 404 is the same outcome as a 204 from the roster's point of view:
        // whatever it was, it is not on this account now. Anything else leaves
        // the row where it is, with `handle` having shown why.
        if (deleted.ok || deleted.refusal.kind === 'no-such-character') {
          setCharacters((current) => current.filter((character) => character.id !== characterId));
        }
      });
    },
    [api, attempt, handle, token],
  );

  const onPlay = useCallback((characterId: string): void => {
    setRefusal(null);
    setOutcome(null);
    // Chosen, not descended. This is the whole of the change: what a character
    // is picked *for* is town, and a run is a press away from there.
    setDescent(null);
    setState((current) => chose(current, characterId));
  }, []);

  /**
   * Town asked for a run.
   *
   * The last outcome is dropped first: whatever the previous press came to —
   * refused, or a run that ended in a death — this is a new one, and a message
   * left standing under a canvas that is opening is about a press that has
   * already been answered.
   *
   * The **choice rides on the press** rather than being reported to this shell
   * as it is made, so there is no state here that could be a render behind the
   * control (`ui/town-screen.tsx`). It is carried straight to the create
   * options and read nowhere else.
   */
  const onDescend = useCallback((visibility: RunVisibility): void => {
    setOutcome(null);
    setDescent({ kind: 'created', visibility });
  }, []);

  /**
   * The board found somebody else's run, and this is where it is.
   *
   * A **room id**, not a code: the board has already resolved one against the
   * directory, which is where the party's fullness was checked — a moment ago
   * rather than whenever the list was drawn (`ui/lobby-board.tsx`). This shell
   * does not re-check anything and could not: the only thing that knows whether
   * a seat is free is the room.
   *
   * It ends in the same place a press of Descend does, on purpose. The last
   * outcome is dropped for the same reason too: a sentence about the last run is
   * about a press that has already been answered.
   */
  const onJoinRun = useCallback((roomId: string): void => {
    setOutcome(null);
    setDescent({ kind: 'joined', roomId });
  }, []);

  /**
   * The room would not open the run, for a reason that is not about who this
   * player is.
   *
   * Straight back to town with the reason, which is the whole point of having a
   * town: the alternative — and what this client did until today — is a message
   * about a server being down, drawn where the world would have been, with
   * nothing on the screen to press.
   *
   * `useCallback` with no dependencies because `GameCanvas` holds it in an
   * effect: a new function each render would tear the room down and rejoin it.
   */
  const onJoinRefused = useCallback((refused: JoinRefusal): void => {
    setOutcome(joinRefused(refused));
    setDescent(null);
  }, []);

  /**
   * The run is over: the player died, the room said what it cost, and the seat
   * was closed (`game/run-end.ts`).
   *
   * Three lines and every one of them is a different fact. The **toll** goes on
   * the outcome, where town's live region reads it — it is passed through
   * untouched, because it is already exactly what the room charged. The screen
   * goes back to **town**, which is where a character between runs is. And the
   * roster is marked **unread**, so the effect above fetches it again: the toll
   * says what was taken and the roster is the only thing that knows what is
   * left, and the row this tab holds was fetched before the descent.
   *
   * Nothing here combines the two. See the header for why the held row is not
   * drawn in the meantime, and why subtracting the toll from it would be a
   * balance no player owns.
   *
   * `useCallback` with no dependencies for the reason `onJoinRefused` has none:
   * `GameCanvas` holds it in an effect, and a new function each render would
   * tear the room down and rejoin it.
   */
  const onRunEnded = useCallback((toll: RunToll): void => {
    setOutcome(runEnded(toll));
    setDescent(null);
    setRosterLoaded(false);
  }, []);

  /**
   * The character as the API answered a town exchange with.
   *
   * The row is **replaced**, not adjusted: what comes back is the whole
   * character as the server now has it, and the alternative — taking the price
   * off the gold this tab is holding — is the arithmetic the header refuses.
   * Matched by id rather than assumed to be the chosen one, so an answer that
   * arrived late for a character the player has already left cannot rewrite
   * somebody else's row.
   */
  const onVendorCharacter = useCallback((character: CharacterSummary): void => {
    setCharacters((current) => current.map((row) => (row.id === character.id ? character : row)));
  }, []);

  const onSignOut = useCallback((): void => {
    setCharacters([]);
    setRosterLoaded(false);
    setRefusal(null);
    setOutcome(null);
    setDescent(null);
    setState(signedOut());
  }, []);

  if (state.status === 'restoring') {
    return (
      <section className="panel" data-testid="restoring">
        <p className="panel__lede">Checking your session…</p>
      </section>
    );
  }

  if (state.status === 'signed-out') {
    return (
      <SignInScreen reason={state.reason} onSignIn={onSignIn} onRegister={onRegister} busy={busy} refusal={refusal} />
    );
  }

  if (state.characterId !== null) {
    // The row town draws and the class the skill tree needs, both from the
    // roster — nothing on the wire carries a class (`PlayerState` has no field
    // for one, and the progression message carries the ledger rather than the
    // catalogue) and the roster answered the question when the character was
    // chosen.
    const chosen = characters.find((character) => character.id === state.characterId) ?? null;

    if (descent !== null) {
      return (
        <GameCanvas
          token={state.token}
          characterId={state.characterId}
          characterClass={chosen?.characterClass ?? null}
          // Which run, and — only when this tab is making one — who may find
          // it. The two are never both meaningful: a room that already exists
          // decided its own visibility when it was created (`Descent`).
          roomId={descent.kind === 'joined' ? descent.roomId : null}
          visibility={descent.kind === 'created' ? descent.visibility : null}
          onSessionEnded={endSession}
          onJoinRefused={onJoinRefused}
          onRunEnded={onRunEnded}
        />
      );
    }

    // A restored session, in the window between "this tab remembers a character"
    // and the roster that says who they are — or a run that has just ended,
    // which puts this tab in the same position on purpose: the row it holds is
    // one the death has already changed. Town is drawn from a row; drawing one
    // from nothing would name nobody and show an empty purse, and drawing one
    // from the row above would show a pre-death purse beside a sentence that
    // has just named, to the gold piece, what was taken out of it. See the
    // header: the server's write-before-close fixed the roster's *answer*, not
    // the age of the row this tab is still holding.
    if (chosen === null || !rosterLoaded) {
      return (
        <section className="panel" data-testid="town-loading">
          <p className="panel__lede">Reading your character…</p>
        </section>
      );
    }

    return (
      <TownScreen
        // Every number resolved here, out of core (`../ui/town-view.ts`). The
        // screen draws what it is given and computes no rule.
        character={townViewOf(chosen)}
        onDescend={onDescend}
        onSignOut={onSignOut}
        // `descending` is deliberately **not** passed, and absent is the state
        // it documents: "a player standing in town". This shell swaps the
        // screen on the same update that asks for the run, so town is never the
        // screen a run is opening *on* — the compiler says as much, since
        // `descent` is narrowed to `null` by the branch above. The screen keeps the
        // guard, and a caller that ever defers the swap (a party lobby, a
        // confirmation) has it ready without a latch anywhere.
        outcome={outcome}
        // The board of other people's runs, mounted as town's child for the
        // reason the counter is: it then exists exactly as long as town does,
        // so a directory read is fresh on every visit and nothing here has to
        // remember to invalidate one. It hands back a **room id** it has just
        // resolved from a code, and this shell opens the socket.
        lobby={
          <LobbyBoard runs={runs} token={state.token} onJoin={onJoinRun} onSessionEnded={endSession} />
        }
        // Mounted as town's child, so it lives exactly as long as town does and
        // reads the character afresh on every visit. See the header.
        vendor={
          <VendorCounter
            api={api}
            token={state.token}
            characterId={state.characterId}
            onCharacter={onVendorCharacter}
            onSessionEnded={endSession}
          />
        }
      />
    );
  }

  return (
    <CharacterScreen
      displayName={state.account.displayName}
      characters={characters}
      onPlay={onPlay}
      onCreate={onCreate}
      onDelete={onDelete}
      onSignOut={onSignOut}
      busy={busy}
      refusal={refusal}
      loading={!rosterLoaded}
    />
  );
};
